import { useCallback } from 'react';
import { graphql } from 'react-relay';

import { ConfluenceFolderAri } from '@atlassian/ari/confluence';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { rememberSmartFolderAutoSync } from '../smartFolderAutoSyncFreshness.ts';

import type {
	useSyncSmartFolderMutation$data,
	useSyncSmartFolderMutation$variables,
} from './__generated__/useSyncSmartFolderMutation.graphql.ts';
import { useApplySmartFolderSubTypeChange } from './useApplySmartFolderSubTypeChange.ts';

type SyncSmartFolderInput = {
	folderId: string;
	force?: boolean;
};

type UseSyncSmartFolderResult = {
	syncSmartFolder: (
		input: SyncSmartFolderInput,
	) => Promise<{ data?: useSyncSmartFolderMutation$data }>;
	called: boolean;
	data: useSyncSmartFolderMutation$data | undefined;
	error: Error | undefined;
	loading: boolean;
};

export const isSuccessfulSmartFolderSyncPayload = (
	payload: useSyncSmartFolderMutation$data['confluence_syncSmartFolder'] | undefined | null,
): payload is NonNullable<useSyncSmartFolderMutation$data['confluence_syncSmartFolder']> =>
	Boolean(payload?.success && !payload.errors?.length);

export const useSyncSmartFolder = (): UseSyncSmartFolderResult => {
	const { activationId, cloudId } = useSessionData();

	const [syncSmartFolderMutation, { called, data, error, loading }] = useMutationInterop<
		useSyncSmartFolderMutation$data,
		useSyncSmartFolderMutation$variables
	>(graphql`
		mutation useSyncSmartFolderMutation($cloudId: ID!, $input: ConfluenceSyncSmartFolderInput!) {
			confluence_syncSmartFolder(cloudId: $cloudId, input: $input) {
				errors {
					message
					extensions {
						__typename
						errorType
						statusCode
					}
				}
				success
			}
		}
	`);

	const applySmartFolderSubTypeChange = useApplySmartFolderSubTypeChange();

	const syncSmartFolder = useCallback(
		async ({ folderId, force }: SyncSmartFolderInput) => {
			const folderAri = ConfluenceFolderAri.create({
				activationId,
				folderId,
				siteId: cloudId,
			}).toString();

			const result = await syncSmartFolderMutation({
				variables: {
					cloudId,
					input: {
						folderId: folderAri,
						...(force !== undefined ? { force } : {}),
					},
				},
			});

			const payload = result.data?.confluence_syncSmartFolder;
			if (payload?.success) {
				rememberSmartFolderAutoSync(folderId);
			}
			if (isSuccessfulSmartFolderSyncPayload(payload)) {
				applySmartFolderSubTypeChange({ folderId, subType: 'managed' });
			}

			return result;
		},
		[activationId, cloudId, syncSmartFolderMutation, applySmartFolderSubTypeChange],
	);

	return {
		syncSmartFolder,
		called,
		data,
		error,
		loading,
	};
};
